import {
  ColoredCircle,
  ContentContainer,
  EllipsedText,
  FlexColumn,
  FlexResponsive,
  FlexRow,
  LabeledData,
  LabeledInput,
  MaterialIcon,
  useModal,
} from '@gorila-shared-ui/components';
import { Button, ButtonOverrides } from 'baseui/button';
import { StyledLink } from 'baseui/link';
import { LabelSmall, ParagraphSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { updateJobExpenses, updateJobOverprice } from '../../services/jobsService';
import { updateJobModalState } from '../../storage/jobStates';
import { Job } from '../../types/jobs';
import { preventMinus } from '../../utils/formUtilis';
import ConfirmModal from '../shared/ConfirmModal';
import StyledTooltip from '../ui/StyledTooltip';

type Props = {
  job: Job;
};

export function JobResume({ job }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const { closeModal: closeOverpriceModal, openModal: openOverpriceModal, showModal: showOverpriceModal } = useModal();
  const { closeModal: closeExpensesModal, openModal: openExpensesModal, showModal: showExpensesModal } = useModal();
  const [expenses, setExpenses] = useState<number>();
  const [overprice, setOverprice] = useState<number>();
  const setUpdateJob = useSetRecoilState(updateJobModalState);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const editButtonOverrides: ButtonOverrides = {
    Root: {
      style: {
        width: theme.sizing.scale700,
        height: theme.sizing.scale700,
      },
    },
  };

  useEffect(() => {
    setExpenses(job.expenses);
  }, [job.expenses]);

  useEffect(() => {
    setOverprice(job.overprice);
  }, [job.overprice]);

  const onEditOverprice = async () => {
    const { error } = await updateJobOverprice(job._id, overprice ?? 0);
    if (!error) {
      showPositiveFeedback(`${FEEDBACK_PREFIXES.overprice} se actualizó correctamente.`);
      closeOverpriceModal();
      setUpdateJob('update');
    } else {
      showFailFeedback(FEEDBACK.failedEdition(FEEDBACK_PREFIXES.overprice));
    }
  };

  const onCloseOverprice = () => {
    closeOverpriceModal();
    setTimeout(() => {
      setOverprice(job.overprice);
    }, 500);
  };

  const onEditExpenses = async () => {
    const { error } = await updateJobExpenses(job._id, expenses ?? 0);
    if (!error) {
      showPositiveFeedback(`${FEEDBACK_PREFIXES.expenses} se actualizaron correctamente.`);
      closeExpensesModal();
      setUpdateJob('update');
    } else {
      showFailFeedback(FEEDBACK.failedEdition(FEEDBACK_PREFIXES.expenses));
    }
  };

  const onCloseExpenses = () => {
    closeExpensesModal();
    setTimeout(() => {
      setExpenses(job.expenses);
    }, 500);
  };

  return (
    <ContentContainer>
      <FlexResponsive gap={theme.sizing.scale200}>
        <FlexColumn
          gap={theme.sizing.scale200}
          classNames={css({ width: '100%' })}
        >
          <LabeledData
            label={'Tipo'}
            content={<EllipsedText maxLines={5}>{job.jobType.name ?? '-'}</EllipsedText>}
          />
          <LabeledData
            label={'Descripción'}
            content={<EllipsedText maxLines={5}>{job.description ?? '-'}</EllipsedText>}
          />
          <LabeledData
            label={'Usuario'}
            content={<EllipsedText maxLines={5}>{job.user?.name ?? '-'}</EllipsedText>}
          />
          {job.deviceIds?.length > 0 && (
            <LabeledData
              label={'IDs equipos'}
              content={<EllipsedText maxLines={5}>{job.deviceIds.toString()}</EllipsedText>}
            />
          )}
          <LabeledData
            label={'Costo de Trabajo'}
            content={
              <EllipsedText maxLines={5}>{job.cost ? `$${Intl.NumberFormat().format(job.cost)}` : '-'}</EllipsedText>
            }
          />
          <LabeledData
            label={'Viáticos'}
            content={
              <FlexRow
                gap={theme.sizing.scale200}
                centered
              >
                <EllipsedText maxLines={5}>
                  {job.expenses ? `$${Intl.NumberFormat().format(job.expenses)}` : '-'}
                </EllipsedText>
                <StyledTooltip
                  content={'Editar'}
                  showArrow={false}
                >
                  <Button
                    size="mini"
                    shape="square"
                    onClick={openExpensesModal}
                    kind="tertiary"
                    overrides={editButtonOverrides}
                  >
                    <MaterialIcon
                      name={'edit'}
                      size="14px"
                    />
                  </Button>
                </StyledTooltip>
              </FlexRow>
            }
          />
          <LabeledData
            label={'Sobreprecio'}
            content={
              <FlexRow
                gap={theme.sizing.scale200}
                centered
              >
                <EllipsedText maxLines={5}>
                  {job.overprice ? `$${Intl.NumberFormat().format(job.overprice)}` : '-'}
                </EllipsedText>
                <StyledTooltip
                  content={'Editar'}
                  showArrow={false}
                >
                  <Button
                    size="mini"
                    shape="square"
                    onClick={openOverpriceModal}
                    kind="tertiary"
                    overrides={editButtonOverrides}
                  >
                    <MaterialIcon
                      name={'edit'}
                      size="14px"
                    />
                  </Button>
                </StyledTooltip>
              </FlexRow>
            }
          />
          <LabeledData
            label={'Total'}
            content={
              <EllipsedText maxLines={5}>{job.price ? `$${Intl.NumberFormat().format(job.price)}` : '-'}</EllipsedText>
            }
          />
        </FlexColumn>
        <FlexColumn classNames={css({ width: '100%' })}>
          <FlexColumn gap={theme.sizing.scale200}>
            <LabeledData
              label={'Compañia'}
              content={<EllipsedText maxLines={5}>{job.tenant?.name ?? '-'}</EllipsedText>}
            />
            <LabeledData
              label={'Estado'}
              content={
                <FlexRow
                  classNames={css({ alignItems: 'center' })}
                  gap={theme.sizing.scale300}
                >
                  <EllipsedText maxLines={5}>{job.status?.name ?? '-'}</EllipsedText>
                  <ColoredCircle color={job.status?.color} />
                </FlexRow>
              }
            />
            <LabeledData
              label={'Georeferencia'}
              content={<EllipsedText maxLines={5}>{job.geocode ? job.geocode : '-'}</EllipsedText>}
            />
            <LabeledData
              label={'Latitud'}
              content={<EllipsedText maxLines={5}>{job.latitude ? `${job.latitude}` : '-'}</EllipsedText>}
            />
            <LabeledData
              label={'Longitud'}
              content={<EllipsedText maxLines={5}>{job.longitude ? `${job.longitude}` : '-'}</EllipsedText>}
            />
            <LabeledData
              label={'ID Externo'}
              content={
                <ParagraphSmall margin={0}>
                  {job.externalId?.length === 24 ? (
                    <StyledLink
                      href={`https://administrativo.gorilamx.com/emisiones/${job.externalId}`}
                      target="_blank"
                    >
                      {job.externalId}
                    </StyledLink>
                  ) : (
                    <EllipsedText maxLines={0}>{job.externalId || '-'}</EllipsedText>
                  )}
                </ParagraphSmall>
              }
            />
          </FlexColumn>
        </FlexColumn>
      </FlexResponsive>
      {job.extraData.length && job.extraData.length !== 0 && (
        <FlexColumn classNames={css({ flex: 1 })}>
          <LabelSmall>Extra data</LabelSmall>
          <FlexColumn gap={theme.sizing.scale200}>
            {job.extraData.map((data, index) => {
              const dataKeys = Object.keys(data);
              return (
                <FlexRow key={index}>
                  {index + 1}.-
                  {dataKeys.map((key, index) => {
                    if (key === 'visible') return null;
                    return (
                      <LabeledData
                        key={key}
                        label={`${key[0].toUpperCase()}${key.slice(1)}:`}
                        labelWidth="60px"
                        content={
                          <FlexRow classNames={css({ alignItems: 'center' })}>
                            <EllipsedText maxLines={5}>{data[key] || '-'}</EllipsedText>
                            {dataKeys[index + 1] === 'visible' && (
                              <StyledTooltip
                                content={data[dataKeys[index + 1]] === '1' ? 'Visible' : 'No visible'}
                                showArrow={false}
                              >
                                <MaterialIcon
                                  name={data[dataKeys[index + 1]] === '1' ? 'done' : 'close'}
                                  color={
                                    data[dataKeys[index + 1]] === '1' ? theme.colors.positive : theme.colors.negative
                                  }
                                  size="18px"
                                />
                              </StyledTooltip>
                            )}
                          </FlexRow>
                        }
                      />
                    );
                  })}
                </FlexRow>
              );
            })}
          </FlexColumn>
        </FlexColumn>
      )}
      <ConfirmModal
        description={
          <FlexColumn>
            <LabeledInput
              onChange={(value) => setExpenses(value ? +value : undefined)}
              type="number"
              startEnhancer="$"
              onKeyDown={preventMinus}
              min={0}
              value={expenses?.toString() ?? ''}
            />
          </FlexColumn>
        }
        isOpen={showExpensesModal}
        onConfirm={onEditExpenses}
        onCancel={onCloseExpenses}
        title="Editar viáticos"
      />
      <ConfirmModal
        description={
          <FlexColumn>
            <LabeledInput
              onChange={(value) => setOverprice(value ? +value : undefined)}
              type="number"
              startEnhancer="$"
              onKeyDown={preventMinus}
              min={0}
              value={overprice?.toString() ?? ''}
            />
          </FlexColumn>
        }
        isOpen={showOverpriceModal}
        onConfirm={onEditOverprice}
        onCancel={onCloseOverprice}
        title="Editar sobreprecio"
      />
    </ContentContainer>
  );
}
